import { format, isValid, parseISO } from 'date-fns';
import { ISO_DATE_VALIDATION_REGEX } from './constants/regex';

/**
 * Parse a `YYYY-MM-DD` value into a `Date` at local midnight, or `null` if it is
 * not a canonical calendar date.
 *
 * Both checks are load-bearing. The regex rejects the looser inputs `parseISO`
 * accepts (`2024`, `2024-01`, a full datetime), and `parseISO` rejects
 * impossible days such as `2024-02-30`. `parseISO` reads a date-only string as
 * local time, so the calendar day is stable regardless of the viewer's timezone
 * — unlike `new Date(value)`, which reads it as UTC.
 */
export const isoDateToLocalDate = (value: string): Date | null => {
  if (!ISO_DATE_VALIDATION_REGEX.test(value)) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

/**
 * True when `value` is a canonical ISO-8601 full-date (`YYYY-MM-DD`) denoting a
 * real calendar day.
 */
export const isValidIsoDate = (value: string): boolean =>
  isoDateToLocalDate(value) !== null;

/** Format a `Date` to a `YYYY-MM-DD` value using its local calendar date. */
export const localDateToIsoDate = (date: Date): string =>
  format(date, 'yyyy-MM-dd');

/**
 * Format an ISO-8601 full-date for display using the runtime locale, returning
 * the original string unchanged if it is not a valid date. Callers that have an
 * account/user locale preference should format via that instead.
 */
export const formatIsoDate = (value: string): string => {
  const date = isoDateToLocalDate(value);
  return date ? date.toLocaleDateString() : value;
};
